import { useEffect } from 'react';
import { X, TrendingUp, TrendingDown, CheckCircle2, Info, Wrench } from 'lucide-react';

const euro = (n) => (Number(n) || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

const RigaVino = ({ r, tipo }) => (
  <div className="border rounded-lg p-3 bg-white" data-testid={`prezzi-ft-${tipo}-${r.id}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">{r.nome}</p>
        {r.cantina && <p className="text-xs text-gray-500 truncate">{r.cantina}</p>}
      </div>
      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${tipo === 'aumento' ? 'bg-red-50 text-red-700' : tipo === 'corretto' ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700'}`}>
        {r.variazione_perc > 0 ? '+' : ''}{r.variazione_perc}%
      </span>
    </div>
    <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
      <p className="text-gray-500">Costo <span className="text-gray-800 font-medium">{euro(r.costo_vecchio)} → {euro(r.costo_nuovo)}</span></p>
      <p className="text-gray-500 text-right">Carta{' '}
        {r.prezzo_carta_nuovo !== r.prezzo_carta_vecchio
          ? <span className="text-gray-900 font-semibold">{euro(r.prezzo_carta_vecchio)} → {euro(r.prezzo_carta_nuovo)}</span>
          : <span className="text-gray-800 font-medium">{euro(r.prezzo_carta_vecchio)} (invariato)</span>}
      </p>
    </div>
  </div>
);

const Sezione = ({ titolo, Icona, colore, righe, tipo }) => !righe.length ? null : (
  <div className="space-y-2">
    <h3 className={`text-sm font-semibold flex items-center gap-1.5 ${colore}`}><Icona className="h-4 w-4" /> {titolo} ({righe.length})</h3>
    {righe.map(r => <RigaVino key={r.id} r={r} tipo={tipo} />)}
  </div>
);

export const AggiornaPrezziFTDialog = ({ riepilogo, onClose }) => {
  useEffect(() => {
    if (!riepilogo) return undefined;
    const esc = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [riepilogo, onClose]);
  if (!riepilogo) return null;
  const { aumenti = [], riduzioni = [], corretti = [], invariati = 0, primo_controllo = 0, vini_controllati = 0, senza_costo = 0 } = riepilogo;
  return (
    <div onClick={onClose} className="fixed inset-0 z-[70] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="prezzi-ft-dialog">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-2xl max-h-[92vh] sm:rounded-2xl rounded-t-2xl flex flex-col shadow-2xl">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b">
          <div>
            <h2 className="text-base md:text-lg font-semibold text-gray-900">Prezzi verificati sulle fatture</h2>
            <p className="text-xs text-gray-500 mt-0.5" data-testid="prezzi-ft-conteggi">
              {vini_controllati} vini controllati · {aumenti.length} aumenti · {riduzioni.length} riduzioni{corretti.length ? ` · ${corretti.length} costi corretti` : ''} · {invariati} invariati{senza_costo ? ` · ${senza_costo} senza costo in fattura` : ''}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" data-testid="prezzi-ft-chiudi"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-stone-50/60">
          {primo_controllo > 0 && (
            <p className="text-xs text-blue-800 bg-blue-50 border border-blue-100 rounded-lg p-3 flex gap-2" data-testid="prezzi-ft-primo-controllo">
              <Info className="h-4 w-4 flex-shrink-0" />
              Primo controllo: ho registrato il costo attuale di {primo_controllo} vini come riferimento. Da ora i prezzi in carta restano fermi e cambiano solo quando premi "Aggiorna da Fatture".
            </p>
          )}
          {!aumenti.length && !riduzioni.length && !corretti.length && (
            <p className="py-8 text-center text-sm text-emerald-700 flex items-center justify-center gap-2" data-testid="prezzi-ft-nessuna-variazione">
              <CheckCircle2 className="h-5 w-5" /> Nessuna variazione di prezzo nelle fatture.
            </p>
          )}
          <Sezione titolo="Aumenti applicati alla carta (stesso ricarico)" Icona={TrendingUp} colore="text-red-700" righe={aumenti} tipo="aumento" />
          <Sezione titolo="Riduzioni (aggiornato solo il costo, la carta non scende)" Icona={TrendingDown} colore="text-emerald-700" righe={riduzioni} tipo="riduzione" />
          <Sezione titolo="Costi corretti (errore di calcolo, nessuna fattura nuova)" Icona={Wrench} colore="text-blue-700" righe={corretti} tipo="corretto" />
        </div>
        <div className="px-5 pt-3 border-t flex justify-end" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm bg-gray-900 text-white hover:bg-gray-800" data-testid="prezzi-ft-ok">Ho capito</button>
        </div>
      </div>
    </div>
  );
};
